import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';

const LOGO = '/images/beautykit-logo.png';

const NAV = [
  { id: 'beautykit', label: 'nav.beautykit' },
  { id: 'chisiamo', label: 'nav.about' },
  { id: 'contatti', label: 'nav.contact' },
];

const CartButton = ({ count, onClick }) => (
  <button onClick={onClick} className="cart-btn" data-testid="cart-button" aria-label="Carrello">
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 7a2 2 0 01-2 2H8a2 2 0 01-2-2L5 9zM9 13h6" />
    </svg>
    <AnimatePresence>
      {count > 0 && (
        <motion.span
          key={count}
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.4, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 500, damping: 22 }}
          className="cart-badge"
          data-testid="cart-count"
        >
          {count}
        </motion.span>
      )}
    </AnimatePresence>
  </button>
);

const Header = ({ activeSection, setActiveSection, cartCount, setShowCartModal }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => { setActiveSection(id); setOpen(false); };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`site-header ${scrolled ? 'is-scrolled' : ''}`}
      data-testid="site-header"
    >
      <div className="site-header-inner">
        <button className="logo-btn" onClick={() => go('hero')} data-testid="logo-home" aria-label="Home">
          <img src={LOGO} alt="BeautyKit" />
        </button>

        <nav className="desktop-nav">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
              data-testid={`nav-${item.id}`}
            >
              {t(item.label)}
              {activeSection === item.id && (
                <motion.span layoutId="nav-underline" className="nav-underline" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
              )}
            </button>
          ))}
        </nav>

        <div className="header-actions">
          <div className="desktop-only"><LanguageSelector /></div>
          <CartButton count={cartCount} onClick={() => setShowCartModal(true)} />
          <button className={`burger ${open ? 'open' : ''}`} onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mobile-menu"
            data-testid="mobile-menu"
          >
            <nav>
              {[{ id: 'hero', label: 'nav.home' }, ...NAV].map((item, i) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i, duration: 0.3 }}
                  onClick={() => go(item.id)}
                  className={`mobile-link ${activeSection === item.id ? 'active' : ''}`}
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {t(item.label)}
                </motion.button>
              ))}
              <div className="mobile-lang"><LanguageSelector testIdSuffix="-mobile" /></div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
